import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { SectionLabel, MaskedHeading, Reveal } from "@/components/site/Motion";

const HOTEL_IMG =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const CASINO_IMG =
  "https://images.unsplash.com/photo-1596838132731-3301c3fd4317?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export const HotelCasino = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section id="hotel" className="py-24 md:py-36 px-6 md:px-12 max-w-[1600px] mx-auto">
      <div className="mb-14">
        <SectionLabel>{t.hotel.overline}</SectionLabel>
        <h2 className="font-heading text-4xl md:text-6xl text-white tracking-tight">
          <MaskedHeading text={t.hotel.title} />
        </h2>
        <Reveal delay={0.1}>
          <p className="mt-5 font-body text-white/60 max-w-2xl leading-relaxed">{t.hotel.desc}</p>
        </Reveal>
      </div>

      <div ref={ref} className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 items-stretch">
        {/* Casino big */}
        <div className="md:col-span-7 relative overflow-hidden h-[340px] md:h-[560px]">
          <motion.img
            style={{ y: y1, scale: 1.15 }}
            src={CASINO_IMG}
            alt="Casino floor"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6">
            <p className="font-display text-xs tracking-[0.3em] uppercase text-brand mb-1">Casino</p>
            <p className="font-heading text-3xl md:text-4xl text-white">{t.hotel.features[1].title}</p>
          </div>
        </div>

        {/* Hotel */}
        <div className="md:col-span-5 relative overflow-hidden h-[340px] md:h-[560px]">
          <motion.img
            style={{ y: y2, scale: 1.15 }}
            src={HOTEL_IMG}
            alt="Partner hotel"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6">
            <p className="font-display text-xs tracking-[0.3em] uppercase text-brand mb-1">Hotel</p>
            <p className="font-heading text-3xl md:text-4xl text-white">{t.hotel.features[0].title}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mt-14">
        {t.hotel.features.map((f, i) => (
          <Reveal key={f.no} delay={i * 0.1}>
            <div className="group bg-surface/40 border border-white/5 hover:border-brand/40 transition-colors p-8 h-full">
              <span className="font-display text-brand text-lg">{f.no}</span>
              <h3 className="font-display text-xl md:text-2xl text-white mt-4 group-hover:translate-x-1 transition-transform">
                {f.title}
              </h3>
              <p className="mt-3 font-body text-white/55 leading-relaxed">{f.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
